import { Component, DestroyRef, inject, input, linkedSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ButtonAddNewComponent } from '../../../../../shared/components/button-add-new/button-add-new.component';
import { ExpansionPanel } from '../../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../../shared/components/toastr/toastr-success.component';
import { LabelRequiredDirective } from '../../../../../shared/directives/label-required.directive';
import { PhaseCreateRequest } from '../../requests/create-request';
import { PhaseStore } from '../../store/phase.store';
import { Référentiel } from '../../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-phases',
  imports: [
    ReactiveFormsModule,
    ExpansionPanel,
    ButtonAddNewComponent,
    HlmInputImports,
    LabelRequiredDirective,
    LabelRequiredDirective,
    HlmFieldImports,
  ],
  templateUrl: './form-phases.component.html',
})
export class FormPhasesComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #phaseStore = inject(PhaseStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(PhaseStore);
  readonly phases = linkedSignal(() => this.#store.entities);

  readonly referentiel = input.required<Référentiel>();

  readonly form = this.#fb.group({
    phases: this.#fb.array([]),
  });

  ngOnInit(): void {
    this.addPhase();
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  addPhase() {
    const phase = this.#fb.group({
      nom: ['', [Validators.required]],
    });
    this.phasesForm.push(phase);
  }

  onSubmit() {
    const toastrId = uuidv7();

    const nouvellesPhases: PhaseCreateRequest = {
      idReferentiel: this.referentiel().id,
      phases: this.phasesForm.value
        .filter((x: { nom: string }) => !!x.nom?.trim())
        .map((x: { nom: string }, idx: number) => ({
          nom: x.nom,
          indice: this.phases().length + idx,
        })),
    };

    if (!nouvellesPhases.phases || nouvellesPhases.phases.length === 0) {
      this.form.markAllAsTouched();
      return;
    }

    this.#store.créer({
      data: nouvellesPhases,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Création des phases réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#phaseStore.charger(this.referentiel().id);
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(nouvellesPhases.phases),
    });
  }

  alreadyExists(
    nouvellesPhases: {
      nom: string;
    }[],
  ) {
    const toutesLesPhases = this.#store.entities().map((x) => x.nom);

    const doublonsAvecPhasesExistantes = toutesLesPhases.filter((x) => nouvellesPhases.map((y) => y.nom).includes(x));

    this.phasesForm.controls.forEach((control) => {
      if (
        doublonsAvecPhasesExistantes.includes(control.value.nom) ||
        this.phasesForm.controls.find((c) => c.value.nom === control.value.nom && c != control)
      ) {
        control.get('nom')?.setErrors({ notUnique: true });
      }
    });
  }

  get phasesForm() {
    return this.form.get('phases') as FormArray;
  }
}
